import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { Sparkles, Loader2, HeartHandshake, MessageSquareText, Send, CheckCheck, Copy, Gem } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../ui/textarea";
import { AiDisclaimer } from "../AiDisclaimer";
import { useAuth } from "../../context/AuthContext";
import { isPremium, startProCheckout } from "../../lib/premium";

export default function AIPanel({ patient, latestDiagnosis, onFinalize, finalizing, onSendToRx }) {
  const { user } = useAuth();
  const premium = isPremium(user);
  const [query, setQuery] = useState(latestDiagnosis || "");
  const [auditOnFinalize, setAuditOnFinalize] = useState(() => premium && localStorage.getItem("clinped_audit_on_finalize") === "1");
  const toggleAudit = () => {
    if (!premium) { toast.info("La revisión final del expediente (NOM) es del Plan Pro.", { action: { label: "Actualizar", onClick: startProCheckout } }); return; }
    const v = !auditOnFinalize;
    setAuditOnFinalize(v);
    localStorage.setItem("clinped_audit_on_finalize", v ? "1" : "0");
  };
  const [freeAns, setFreeAns] = useState("");
  const [parents, setParents] = useState(null);
  const [loadingFree, setLoadingFree] = useState(false);
  const [loadingP, setLoadingP] = useState(false);

  const askFree = async () => {
    if (!query.trim()) { toast.error("Escribe tu consulta (ej. genera un plan de ablactación)"); return; }
    setLoadingFree(true); setFreeAns("");
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "free", context: query });
      setFreeAns(res.data.text || "Sin respuesta.");
    } catch (e) { toast.error("Error de IA"); }
    finally { setLoadingFree(false); }
  };

  const genParents = async () => {
    if (!query.trim()) { toast.error("Escribe un diagnóstico o tema"); return; }
    setLoadingP(true);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "parents", diagnosis: query });
      setParents(res.data.parsed);
      if (!res.data.parsed) toast.error("La IA no devolvió datos");
    } catch (e) { toast.error("Error de IA"); }
    finally { setLoadingP(false); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copiado"); };

  const sendFreeToRx = () => { onSendToRx?.({ items: [{}], indications: freeAns, diagnosis: query }); toast.success("Enviado a indicaciones de la receta"); };
  const sendParentsToRx = () => {
    const lines = [
      ...(parents?.recomendaciones || []).map((r) => `• ${r.titulo}: ${r.detalle}`),
      ...(parents?.cuando_acudir?.length ? ["", "Acudir a urgencias si:", ...parents.cuando_acudir.map((s) => `• ${s}`)] : []),
    ];
    onSendToRx?.({ items: [{}], indications: lines.join("\n"), diagnosis: query });
    toast.success("Enviado a la receta como indicaciones (punto por punto). Puedes generar el PDF/QR sin agregar medicamento.");
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-brand-ink to-[#2d2f44] rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-terracotta/30 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-brand-sand" size={20} />
            <h3 className="font-outfit font-bold text-lg">Asistente clínico IA</h3>
          </div>
          <p className="text-white/60 text-sm mb-4">Consulta libre o recomendaciones para padres. La decisión final es del médico.</p>
          <Textarea data-testid="ai-query-input" value={query} onChange={(e) => setQuery(e.target.value)} rows={2}
            placeholder="Escribe tu consulta o tema (ej. genera un plan de ablactación, manejo de dermatitis del pañal…)"
            className="rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/40" />
          <div className="flex flex-wrap gap-3 mt-4">
            <button data-testid="ai-free-btn" onClick={askFree} disabled={loadingFree}
              className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-60">
              {loadingFree ? <Loader2 size={16} className="animate-spin" /> : <MessageSquareText size={16} />} Consultar IA
            </button>
            <button data-testid="ai-parents-btn" onClick={genParents} disabled={loadingP}
              className="flex items-center gap-2 bg-brand-sage hover:bg-brand-sageDark px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-60">
              {loadingP ? <Loader2 size={16} className="animate-spin" /> : <HeartHandshake size={16} />} Recomendaciones a padres
            </button>
          </div>
        </div>
      </div>

      {freeAns && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)] space-y-3" data-testid="ai-free-result">
          <div className="flex items-center justify-between">
            <h4 className="font-outfit font-bold text-brand-ink flex items-center gap-2"><MessageSquareText size={18} className="text-brand-terracotta" /> Respuesta de la IA</h4>
            <div className="flex items-center gap-2">
              <button data-testid="ai-free-copy" onClick={() => copy(freeAns)} className="p-1.5 rounded-lg text-muted-foreground hover:text-brand-ink hover:bg-muted transition-all" title="Copiar"><Copy size={16} /></button>
              {onSendToRx && <button data-testid="ai-free-send" onClick={sendFreeToRx} className="flex items-center gap-1.5 text-xs font-bold text-brand-terracotta bg-brand-terracotta/10 px-3 py-1.5 rounded-full hover:bg-brand-terracotta/20"><Send size={13} /> A indicaciones</button>}
            </div>
          </div>
          <p className="text-sm text-brand-ink/90 whitespace-pre-line leading-relaxed">{freeAns.replace(/\*\*/g, "").replace(/^#{1,6}\s*/gm, "")}</p>
          <AiDisclaimer />
        </motion.div>
      )}

      {parents && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)] space-y-4" data-testid="ai-parents-result">
          <div className="flex items-center justify-between">
            <h4 className="font-outfit font-bold text-brand-ink flex items-center gap-2"><HeartHandshake size={18} className="text-brand-sage" /> Recomendaciones para los padres</h4>
            {onSendToRx && <button data-testid="ai-parents-send" onClick={sendParentsToRx} className="flex items-center gap-1.5 text-xs font-bold text-brand-sageDark bg-brand-sage/15 px-3 py-1.5 rounded-full hover:bg-brand-sage/25"><Send size={13} /> A la receta</button>}
          </div>
          {parents.recomendaciones?.map((r, i) => (
            <div key={i} className="bg-brand-sage/5 rounded-xl p-3">
              <p className="font-semibold text-brand-ink text-sm">{r.titulo}</p>
              <p className="text-sm text-muted-foreground">{r.detalle}</p>
            </div>
          ))}
          {parents.cuando_acudir?.length > 0 && (
            <div className="bg-destructive/5 rounded-xl p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-destructive mb-1">Cuándo acudir a urgencias</p>
              <ul className="text-sm text-brand-ink/80 list-disc pl-5 space-y-0.5">{parents.cuando_acudir.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          <AiDisclaimer />
        </motion.div>
      )}

      <div className="bg-white rounded-3xl p-5 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
        <label className={`flex items-center gap-2 mb-3 select-none ${premium ? "cursor-pointer" : "cursor-not-allowed opacity-90"}`} data-testid="audit-toggle-label">
          <input type="checkbox" checked={auditOnFinalize} onChange={toggleAudit} disabled={!premium} data-testid="audit-on-finalize-toggle"
            className="w-4 h-4 accent-brand-terracotta" />
          <span className="text-sm text-brand-ink font-semibold flex items-center gap-1.5">
            Revisar la nota con IA al finalizar (NOM-004)
            {!premium && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-brand-terracotta bg-brand-terracotta/10 px-2 py-0.5 rounded-full"><Gem size={10} /> Pro</span>}
          </span>
        </label>
        {premium ? (
          <p className="text-xs text-muted-foreground mb-3">
            {auditOnFinalize
              ? "Activado: al finalizar, la IA revisará la nota y sugerirá mejoras según la norma."
              : "Desactivado: la consulta se finaliza directo. Actívalo cuando quieras una revisión de la IA."}
          </p>
        ) : (
          <button data-testid="audit-upgrade-btn" onClick={startProCheckout}
            className="text-xs font-semibold text-brand-terracotta hover:underline mb-3 block">
            La revisión final del expediente es del Plan Pro. Actualizar →
          </button>
        )}
        <button data-testid="finalize-consultation" onClick={() => onFinalize?.()} disabled={finalizing}
          className="w-full flex items-center justify-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3 rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60">
          {finalizing ? <Loader2 size={18} className="animate-spin" /> : <CheckCheck size={18} />} {finalizing ? "Finalizando…" : "Finalizar consulta"}
        </button>
      </div>
    </div>
  );
}
